import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import PublishOutlined from '@mui/icons-material/PublishOutlined';
import UnpublishedOutlined from '@mui/icons-material/UnpublishedOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  BOOK_LANGUAGE_FORM_EN_ID,
} from '../../Settings/lists/bookLanguageFormSupport';
import {
  authorRowSummaryLabel,
  bookLanguageRowIsActive,
  categoryRowSummaryLabel,
  ageGroupIntervalLabel,
  settingsListApiError,
} from '../../Settings/lists/singleTableListSupport';
import { translateText } from '../../Settings/lists/translationApi';
import { publishBook } from '../bookApi';
import BookPreview from '../BookPreview';
import BookFormDraftToolbar from './BookFormDraftToolbar';
import BookFormStepDescriptions from './BookFormStepDescriptions';
import BookFormStepDetails from './BookFormStepDetails';
import BookFormStepLanguages from './BookFormStepLanguages';
import BookFormStepNavigation from './BookFormStepNavigation';
import BookFormStepImages from './BookFormStepImages';
import BookFormStepPages from './BookFormStepPages';
import BookFormStepTitle from './BookFormStepTitle';
import { instance } from '../../../axios/AxiosInstance';
import {
  bookCreateDraftHasSignificantContent,
  clearBookCreateDraftStorage,
  emptyBookFormDraft,
  hasPersistedBookCreateDraft,
} from './bookFormStorage';
import { computeBookFormStepCompletion } from './bookFormStepCompletion';
import {
  BOOK_PAGE_IMAGE_HEIGHT_PX,
  BOOK_PAGE_IMAGE_WIDTH_PX,
  STEP_LABELS,
} from './constants';
import {
  clonePageDraft,
  defaultPageLangContent,
  ensurePageLangKeys,
  newBookPageDraft,
  targetCodesForTranslate,
} from './model';
import type { BookFormProps, BookPageDraft, BookPageLangContent } from './types';

type WriterOption = {
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  active?: boolean;
};

function normalizeWriterEmailUi(raw: string): string {
  return raw.trim().toLowerCase();
}

export default function BookFormContainer({
  draft,
  onChange,
  languages,
  authors,
  categories,
  ageGroups,
  referenceLoading = false,
  referenceError = null,
  onClearPersistedDraft,
  hideBookPreviewChrome = false,
  validationErrorStep = null,
  isManager = false,
}: BookFormProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPageClientId, setSelectedPageClientId] = useState<string | null>(null);
  const [clearDraftConfirmOpen, setClearDraftConfirmOpen] = useState(false);
  const [bookPreviewOpen, setBookPreviewOpen] = useState(false);
  const [publishDialog, setPublishDialog] = useState<'publish' | 'unpublish' | null>(null);
  const [publishWorking, setPublishWorking] = useState(false);
  const [publishBannerError, setPublishBannerError] = useState<string | null>(null);

  const [writersLoading, setWritersLoading] = useState(false);
  const [writersError, setWritersError] = useState<string | null>(null);
  const [writers, setWriters] = useState<WriterOption[]>([]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) =>
      categoryRowSummaryLabel(a).localeCompare(categoryRowSummaryLabel(b), 'mn'),
    );
  }, [categories]);

  const sortedAgeGroups = useMemo(() => {
    return [...ageGroups].sort((a, b) =>
      ageGroupIntervalLabel(a).localeCompare(ageGroupIntervalLabel(b), 'mn'),
    );
  }, [ageGroups]);

  const sortedAuthors = useMemo(() => {
    return [...authors].sort((a, b) =>
      authorRowSummaryLabel(a).localeCompare(authorRowSummaryLabel(b), 'mn'),
    );
  }, [authors]);

  const enabledLanguages = useMemo(
    () => languages.filter((l) => draft.language_enabled[l.id] && bookLanguageRowIsActive(l)),
    [languages, draft.language_enabled],
  );
  const enabledLangIds = useMemo(() => enabledLanguages.map((l) => l.id), [enabledLanguages]);

  const sortedPages = useMemo(() => {
    return [...draft.pages].sort((a, b) => a.page_number - b.page_number);
  }, [draft.pages]);

  const stepCompleted = useMemo(
    () => computeBookFormStepCompletion(draft, languages),
    [draft, languages],
  );

  useEffect(() => {
    if (sortedPages.length === 0) {
      setSelectedPageClientId(null);
      return;
    }
    if (!selectedPageClientId || !sortedPages.some((p) => p.clientId === selectedPageClientId)) {
      setSelectedPageClientId(sortedPages[0].clientId);
    }
  }, [sortedPages, selectedPageClientId]);

  useEffect(() => {
    if (
      validationErrorStep != null &&
      validationErrorStep >= 0 &&
      validationErrorStep < STEP_LABELS.length
    ) {
      setActiveStep(validationErrorStep);
    }
  }, [validationErrorStep]);

  useLayoutEffect(() => {
    if (draft.mode !== 'create' || languages.length === 0) return;
    if (languages.every((l) => draft.language_enabled[l.id] !== undefined)) return;
    const language_enabled: Record<string, boolean> = { ...draft.language_enabled };
    for (const l of languages) {
      if (language_enabled[l.id] === undefined) {
        language_enabled[l.id] = true;
      }
    }
    const langIds = languages.map((l) => l.id);
    const pages = draft.pages.map((p) => ensurePageLangKeys(p, langIds));
    onChange({ ...draft, language_enabled, pages });
  }, [draft, languages, onChange]);

  const [titleTranslating, setTitleTranslating] = useState(false);
  const [titleTranslateError, setTitleTranslateError] = useState<string | null>(null);
  const [descTranslating, setDescTranslating] = useState(false);
  const [descTranslateError, setDescTranslateError] = useState<string | null>(null);
  const [pageTranslating, setPageTranslating] = useState(false);
  const [pageTranslateError, setPageTranslateError] = useState<string | null>(null);

  const enTitleSource = (draft.name_map[BOOK_LANGUAGE_FORM_EN_ID] ?? '').trim();
  const hasEnEnabled = !!draft.language_enabled[BOOK_LANGUAGE_FORM_EN_ID];
  const otherLanguages = languages.filter((l) => l.id !== BOOK_LANGUAGE_FORM_EN_ID);
  const hasOtherEnabledLangForTitleTranslate = otherLanguages.some((l) => draft.language_enabled[l.id]);
  const showTitleTranslateFromEn = hasEnEnabled && hasOtherEnabledLangForTitleTranslate;
  const canTranslateTitleFromEn = showTitleTranslateFromEn && enTitleSource.length > 0;

  const enDescSource = (draft.description_map[BOOK_LANGUAGE_FORM_EN_ID] ?? '').trim();
  const canTranslateDescFromEn =
    hasEnEnabled && enDescSource.length > 0 && otherLanguages.some((l) => draft.language_enabled[l.id]);

  useEffect(() => {
    setTitleTranslateError(null);
  }, [enTitleSource]);

  useEffect(() => {
    setDescTranslateError(null);
  }, [enDescSource]);

  const fixToggleLanguage = (langId: string, checked: boolean) => {
    const language_enabled = { ...draft.language_enabled, [langId]: checked };
    let pages = draft.pages;
    if (checked) {
      const ids = Array.from(new Set([...enabledLangIds, langId]));
      pages = pages.map((p) => ensurePageLangKeys(p, ids));
    } else {
      pages = pages.map((p) => {
        const { [langId]: _r, ...rest } = p.byLang;
        return { ...p, byLang: rest };
      });
    }
    onChange({ ...draft, language_enabled, pages });
  };

  const handleTranslateTitleFromEn = async () => {
    if (!canTranslateTitleFromEn || titleTranslating) return;
    const targets = targetCodesForTranslate(otherLanguages.filter((l) => draft.language_enabled[l.id]));
    if (targets.length === 0) return;
    setTitleTranslating(true);
    setTitleTranslateError(null);
    try {
      const results = await translateText(enTitleSource, BOOK_LANGUAGE_FORM_EN_ID, targets);
      if (results.length !== targets.length) {
        setTitleTranslateError('Орчуулга бүрэн ирсэнгүй. Дахин оролдоно уу.');
        return;
      }
      const nextMap = { ...draft.name_map };
      otherLanguages
        .filter((l) => draft.language_enabled[l.id])
        .forEach((l, i) => {
          nextMap[l.id] = results[i] ?? '';
        });
      onChange({ ...draft, name_map: nextMap });
    } catch (e: unknown) {
      setTitleTranslateError(settingsListApiError(e, 'Орчуулахад алдаа гарлаа.'));
    } finally {
      setTitleTranslating(false);
    }
  };

  const handleTranslateDescFromEn = async () => {
    if (!canTranslateDescFromEn || descTranslating) return;
    const targets = targetCodesForTranslate(otherLanguages.filter((l) => draft.language_enabled[l.id]));
    if (targets.length === 0) return;
    setDescTranslating(true);
    setDescTranslateError(null);
    try {
      const results = await translateText(enDescSource, BOOK_LANGUAGE_FORM_EN_ID, targets);
      if (results.length !== targets.length) {
        setDescTranslateError('Орчуулга бүрэн ирсэнгүй. Дахин оролдоно уу.');
        return;
      }
      const nextMap = { ...draft.description_map };
      otherLanguages
        .filter((l) => draft.language_enabled[l.id])
        .forEach((l, i) => {
          nextMap[l.id] = results[i] ?? '';
        });
      onChange({ ...draft, description_map: nextMap });
    } catch (e: unknown) {
      setDescTranslateError(settingsListApiError(e, 'Орчуулахад алдаа гарлаа.'));
    } finally {
      setDescTranslating(false);
    }
  };

  const selectedPage = useMemo(
    () => draft.pages.find((p) => p.clientId === selectedPageClientId) ?? null,
    [draft.pages, selectedPageClientId],
  );

  const canTranslatePageFromEn = useMemo(() => {
    if (!selectedPage || !hasEnEnabled || !draft.language_enabled[BOOK_LANGUAGE_FORM_EN_ID]) return false;
    const enContent = selectedPage.byLang[BOOK_LANGUAGE_FORM_EN_ID];
    if (!enContent) return false;
    if (!(enContent.full_text ?? '').trim()) return false;
    return otherLanguages.some((l) => draft.language_enabled[l.id]);
  }, [selectedPage, hasEnEnabled, draft.language_enabled, otherLanguages]);

  const handleTranslatePageTextsFromEn = async () => {
    if (!selectedPage || !canTranslatePageFromEn || pageTranslating) return;
    const enContent = selectedPage.byLang[BOOK_LANGUAGE_FORM_EN_ID];
    if (!enContent) return;
    const source = enContent.full_text.trim();
    if (!source) return;
    const targets = targetCodesForTranslate(otherLanguages.filter((l) => draft.language_enabled[l.id]));
    if (targets.length === 0) return;

    setPageTranslating(true);
    setPageTranslateError(null);
    try {
      const results = await translateText(source, BOOK_LANGUAGE_FORM_EN_ID, targets);
      if (results.length !== targets.length) {
        setPageTranslateError('Орчуулга бүрэн ирсэнгүй. Дахин оролдоно уу.');
        return;
      }
      const nextByLang = { ...selectedPage.byLang };
      otherLanguages
        .filter((l) => draft.language_enabled[l.id])
        .forEach((l, ti) => {
          const cur = nextByLang[l.id] ?? defaultPageLangContent();
          nextByLang[l.id] = { ...cur, full_text: results[ti] ?? '' };
        });
      onChange({
        ...draft,
        pages: draft.pages.map((p) =>
          p.clientId === selectedPage.clientId ? { ...p, byLang: nextByLang } : p,
        ),
      });
    } catch (e: unknown) {
      setPageTranslateError(settingsListApiError(e, 'Орчуулахад алдаа гарлаа.'));
    } finally {
      setPageTranslating(false);
    }
  };

  const setTitleForLang = (langId: string, value: string) => {
    onChange({ ...draft, name_map: { ...draft.name_map, [langId]: value } });
  };

  const setDescriptionForLang = (langId: string, value: string) => {
    onChange({ ...draft, description_map: { ...draft.description_map, [langId]: value } });
  };

  const toggleCategory = (id: string) => {
    const has = draft.category_ids.includes(id);
    onChange({
      ...draft,
      category_ids: has ? draft.category_ids.filter((x) => x !== id) : [...draft.category_ids, id],
    });
  };

  const toggleAgeGroup = (id: string) => {
    const has = draft.age_group_ids.includes(id);
    onChange({
      ...draft,
      age_group_ids: has ? draft.age_group_ids.filter((x) => x !== id) : [...draft.age_group_ids, id],
    });
  };

  const setAuthor = (author_id: string) => {
    onChange({ ...draft, author_id });
  };

  const nextPageNumber = useCallback(() => {
    if (draft.pages.length === 0) return 1;
    return Math.max(...draft.pages.map((p) => p.page_number)) + 1;
  }, [draft.pages]);

  const addPage = () => {
    const n = nextPageNumber();
    const page = newBookPageDraft(enabledLangIds, n);
    onChange({ ...draft, pages: [...draft.pages, page] });
    setSelectedPageClientId(page.clientId);
  };

  const duplicateSelectedPage = () => {
    if (!selectedPage) return;
    const n = nextPageNumber();
    const copy = clonePageDraft(selectedPage, n, enabledLangIds);
    onChange({ ...draft, pages: [...draft.pages, copy] });
    setSelectedPageClientId(copy.clientId);
  };

  const renumberPagesInOrder = (ordered: BookPageDraft[]) =>
    ordered.map((p, i) => ({ ...p, page_number: i + 1 }));

  const deletePageByClientId = (clientId: string) => {
    const sorted = [...draft.pages].sort((a, b) => a.page_number - b.page_number);
    const delIdx = sorted.findIndex((p) => p.clientId === clientId);
    if (delIdx < 0) return;
    const filtered = sorted.filter((p) => p.clientId !== clientId);
    const pages = renumberPagesInOrder(filtered);
    onChange({ ...draft, pages });
    if (selectedPageClientId === clientId) {
      const nextId =
        filtered.length === 0
          ? null
          : filtered[Math.min(delIdx, filtered.length - 1)]?.clientId ?? null;
      setSelectedPageClientId(nextId);
    }
  };

  const reorderPagesByClientIds = (orderedClientIds: string[]) => {
    const byId = new Map(draft.pages.map((p) => [p.clientId, p]));
    const ordered: BookPageDraft[] = [];
    for (const id of orderedClientIds) {
      const page = byId.get(id);
      if (page) ordered.push(page);
    }
    if (ordered.length !== draft.pages.length) return;
    onChange({ ...draft, pages: renumberPagesInOrder(ordered) });
  };

  const updatePageByClientId = (clientId: string, patch: Partial<BookPageDraft>) => {
    onChange({
      ...draft,
      pages: draft.pages.map((p) => (p.clientId === clientId ? { ...p, ...patch } : p)),
    });
  };

  const updatePageLangContent = (
    clientId: string,
    langId: string,
    updater: (prev: BookPageLangContent) => BookPageLangContent,
  ) => {
    onChange({
      ...draft,
      pages: draft.pages.map((p) => {
        if (p.clientId !== clientId) return p;
        const prev = p.byLang[langId] ?? defaultPageLangContent();
        return {
          ...p,
          byLang: { ...p.byLang, [langId]: updater(prev) },
        };
      }),
    });
  };

  const setPageFullText = (clientId: string, langId: string, value: string) => {
    updatePageLangContent(clientId, langId, (prev) => ({ ...prev, full_text: value }));
  };

  const stepNavigation = useMemo(
    () => (
      <BookFormStepNavigation
        activeStep={activeStep}
        onBack={() => setActiveStep((s) => s - 1)}
        onNext={() => setActiveStep((s) => Math.min(STEP_LABELS.length - 1, s + 1))}
      />
    ),
    [activeStep],
  );

  const showClearPersisted =
    draft.mode === 'create' &&
    (hasPersistedBookCreateDraft() || bookCreateDraftHasSignificantContent(draft));

  const showManagerPublish = draft.mode === 'edit' && isManager && draft.id.trim().length > 0;

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    setWritersLoading(true);
    setWritersError(null);
    instance
      .get<{ data?: unknown }>('managers')
      .then((res) => {
        const rows = (res.data as any)?.data;
        const list: WriterOption[] = Array.isArray(rows)
          ? rows
              .filter((u: any) => u && typeof u === 'object' && String(u.role ?? '').toUpperCase() === 'WRITER')
              .map((u: any) => ({
                email: String(u.email ?? ''),
                first_name: String(u.first_name ?? ''),
                last_name: String(u.last_name ?? ''),
                role: String(u.role ?? ''),
                active: u.active !== false,
              }))
              .filter((u) => u.email.trim().length > 0)
          : [];
        if (!cancelled) setWriters(list);
      })
      .catch(() => {
        if (!cancelled) setWritersError('Бичээчдийн жагсаалт ачааллахад алдаа гарлаа.');
      })
      .finally(() => {
        if (!cancelled) setWritersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isManager]);

  const selectedWriters = useMemo(() => {
    const emails = new Set((draft.writer_emails ?? []).map((e) => normalizeWriterEmailUi(String(e))));
    return writers.filter((w) => emails.has(normalizeWriterEmailUi(w.email)));
  }, [writers, draft.writer_emails]);

  const setWriterEmails = (emails: string[]) => {
    onChange({
      ...draft,
      writer_emails: Array.from(
        new Set(emails.map((e) => normalizeWriterEmailUi(String(e))).filter((e) => e.length > 0)),
      ),
    });
  };

  const runConfirmedPublishChange = async () => {
    if (!publishDialog || !showManagerPublish) return;
    const nextPublished = publishDialog === 'publish';
    setPublishWorking(true);
    setPublishBannerError(null);
    try {
      await publishBook(draft.id, nextPublished);
      onChange({ ...draft, published: nextPublished });
      setPublishDialog(null);
    } catch (e: unknown) {
      setPublishBannerError(settingsListApiError(e, 'Төлөв өөрчлөхөд алдаа гарлаа.'));
    } finally {
      setPublishWorking(false);
    }
  };

  const runClearPersistedDraft = () => {
    onClearPersistedDraft?.();
    clearBookCreateDraftStorage();
    setActiveStep(0);
    setSelectedPageClientId(null);
    onChange(emptyBookFormDraft());
    setClearDraftConfirmOpen(false);
  };

  const formBody = (
    <Stack spacing={2}>
      {!hideBookPreviewChrome ? (
        <BookPreview
          open={bookPreviewOpen}
          onClose={() => setBookPreviewOpen(false)}
          draft={draft}
          authors={authors}
          languages={languages}
        />
      ) : null}

      {referenceError ? <Alert severity="warning">{referenceError}</Alert> : null}

      {showManagerPublish ? (
        <>
          <Dialog
            open={publishDialog != null}
            onClose={() => {
              if (publishWorking) return;
              setPublishDialog(null);
            }}
            disableEscapeKeyDown={publishWorking}
            aria-labelledby="book-publish-dialog-title"
            aria-describedby="book-publish-dialog-desc"
          >
            <DialogTitle id="book-publish-dialog-title">
              {publishDialog === 'publish' ? 'Ном нийтлэх' : 'Нийтлэлийг буцаах'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="book-publish-dialog-desc">
                {publishDialog === 'publish'
                  ? 'Энэ номыг нийтэлбэл уншигчдад харагдана. Үргэлжлүүлэх үү?'
                  : 'Ноороглоход ном апп дээрх уншигчдаас нуугдана. Үргэлжлүүлэх үү?'}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button type="button" onClick={() => setPublishDialog(null)} disabled={publishWorking}>
                Болих
              </Button>
              <Button
                type="button"
                variant="contained"
                color={publishDialog === 'unpublish' ? 'warning' : 'success'}
                onClick={() => void runConfirmedPublishChange()}
                disabled={publishWorking}
                autoFocus
              >
                {publishWorking
                  ? 'Илгээж байна…'
                  : publishDialog === 'publish'
                    ? 'Тийм, нийтлэх'
                    : 'Тийм, ноороглох'}
              </Button>
            </DialogActions>
          </Dialog>

          {publishBannerError ? (
            <Alert severity="error" onClose={() => setPublishBannerError(null)} sx={{ py: 0.5 }}>
              {publishBannerError}
            </Alert>
          ) : null}

          <Paper
            elevation={0}
            variant="outlined"
            sx={(theme) => ({
              p: 2,
              borderLeftWidth: 4,
              borderLeftStyle: 'solid',
              borderLeftColor: draft.published ? theme.palette.success.main : theme.palette.divider,
              bgcolor: theme.palette.mode === 'dark' ? 'action.selected' : 'action.hover',
            })}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={draft.published ? 'Нийтлэгдсэн' : 'Ноорог'}
                  variant={draft.published ? 'filled' : 'outlined'}
                  sx={(theme) =>
                    draft.published
                      ? {
                          bgcolor: 'success.main',
                          color: theme.palette.getContrastText(theme.palette.success.main),
                          fontWeight: 600,
                          '& .MuiChip-label': { color: 'inherit' },
                        }
                      : {
                          color: 'text.primary',
                          borderColor: 'text.secondary',
                          bgcolor: 'background.paper',
                          fontWeight: 600,
                          '& .MuiChip-label': { color: 'inherit' },
                        }
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  Менежер — нийтлэх эсвэл ноороглох
                </Typography>
              </Stack>
              <Button
                type="button"
                variant="contained"
                size="medium"
                color={draft.published ? 'warning' : 'success'}
                startIcon={draft.published ? <UnpublishedOutlined /> : <PublishOutlined />}
                onClick={() => {
                  setPublishBannerError(null);
                  setPublishDialog(draft.published ? 'unpublish' : 'publish');
                }}
                disabled={publishWorking}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, textTransform: 'none', fontWeight: 700 }}
              >
                {draft.published ? 'Ноороглох' : 'Нийтлэх'}
              </Button>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                Номын бичээчид (WRITER)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Энэ номыг ямар бичээчид засах эрхтэйг сонгоно.
              </Typography>

              {writersError ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {writersError}
                </Alert>
              ) : null}

              <Autocomplete
                multiple
                options={writers}
                value={selectedWriters}
                loading={writersLoading}
                onChange={(_e, value) => setWriterEmails(value.map((v) => v.email))}
                getOptionLabel={(o) => `${o.first_name} ${o.last_name}`.trim() + (o.email ? ` — ${o.email}` : '')}
                isOptionEqualToValue={(a, b) => normalizeWriterEmailUi(a.email) === normalizeWriterEmailUi(b.email)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Бичээч сонгох"
                    placeholder={writersLoading ? 'Ачааллаж байна…' : 'Бичээч нэмэх'}
                  />
                )}
              />

              {selectedWriters.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Одоогоор оноогдсон бичээчид
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {selectedWriters.map((w) => {
                      const fullName = `${w.first_name} ${w.last_name}`.trim();
                      const chipLabel = fullName ? `${fullName} — ${w.email}` : w.email;
                      return (
                      <Chip
                        key={w.email}
                        size="small"
                        label={chipLabel}
                        title={w.email}
                        onDelete={() =>
                          setWriterEmails(
                            (draft.writer_emails ?? []).filter(
                              (e) => normalizeWriterEmailUi(String(e)) !== normalizeWriterEmailUi(w.email),
                            ),
                          )
                        }
                      />
                    );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Бичээч оноогоогүй байна.
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      ) : null}

      <BookFormDraftToolbar
        showPersistedBanner={showClearPersisted}
        clearConfirmOpen={clearDraftConfirmOpen}
        onOpenClearConfirm={() => setClearDraftConfirmOpen(true)}
        onCloseClearConfirm={() => setClearDraftConfirmOpen(false)}
        onConfirmClear={runClearPersistedDraft}
      />

      <Stepper activeStep={activeStep} nonLinear sx={{ mb: 1 }}>
        {STEP_LABELS.map((label, index) => {
          const stepHasError = validationErrorStep != null && index === validationErrorStep;
          const isCurrent = index === activeStep;
          return (
            <Step key={label} completed={stepCompleted[index] ?? false}>
              <StepButton
                color="inherit"
                onClick={() => setActiveStep(index)}
                sx={(theme) => ({
                  '& .MuiStepLabel-label': {
                    fontSize: isCurrent
                      ? theme.typography.subtitle1.fontSize
                      : theme.typography.body2.fontSize,
                    lineHeight: isCurrent
                      ? theme.typography.subtitle1.lineHeight
                      : theme.typography.body2.lineHeight,
                    fontWeight: stepHasError ? 600 : isCurrent ? 700 : 500,
                    ...(stepHasError ? { color: 'error.main' } : {}),
                  },
                  '& .MuiStepIcon-root': {
                    fontSize: isCurrent ? theme.typography.h6.fontSize : theme.typography.body1.fontSize,
                    ...(stepHasError ? { color: 'error.main' } : {}),
                  },
                })}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>

      {draft.mode === 'edit' && activeStep === 0 && (
        <TextField label="ID" name="id" value={draft.id} disabled autoComplete="off" fullWidth margin="none" />
      )}

      {activeStep === 0 && (
        <BookFormStepLanguages
          draft={draft}
          languages={languages}
          onToggleLanguage={fixToggleLanguage}
          stepNavigation={stepNavigation}
        />
      )}

      {activeStep === 1 && (
        <BookFormStepDetails
          draft={draft}
          referenceLoading={referenceLoading}
          sortedAuthors={sortedAuthors}
          sortedCategories={sortedCategories}
          sortedAgeGroups={sortedAgeGroups}
          setAuthor={setAuthor}
          toggleCategory={toggleCategory}
          toggleAgeGroup={toggleAgeGroup}
          stepNavigation={stepNavigation}
        />
      )}

      {activeStep === 2 && (
        <BookFormStepImages
          draft={draft}
          onChange={onChange}
          enabledLanguages={enabledLanguages}
          stepNavigation={stepNavigation}
        />
      )}

      {activeStep === 3 && (
        <BookFormStepTitle
          draft={draft}
          enabledLanguages={enabledLanguages}
          hasEnEnabled={hasEnEnabled}
          hasOtherEnabledLangForTitleTranslate={hasOtherEnabledLangForTitleTranslate}
          showTitleTranslateFromEn={showTitleTranslateFromEn}
          canTranslateTitleFromEn={canTranslateTitleFromEn}
          titleTranslating={titleTranslating}
          titleTranslateError={titleTranslateError}
          onDismissTitleTranslateError={() => setTitleTranslateError(null)}
          onTranslateTitleFromEn={handleTranslateTitleFromEn}
          setTitleForLang={setTitleForLang}
          stepNavigation={stepNavigation}
        />
      )}

      {activeStep === 4 && (
        <BookFormStepDescriptions
          draft={draft}
          enabledLanguages={enabledLanguages}
          canTranslateDescFromEn={canTranslateDescFromEn}
          descTranslating={descTranslating}
          descTranslateError={descTranslateError}
          onDismissDescTranslateError={() => setDescTranslateError(null)}
          onTranslateDescFromEn={handleTranslateDescFromEn}
          setDescriptionForLang={setDescriptionForLang}
          stepNavigation={stepNavigation}
        />
      )}

      {activeStep === 5 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Хуудас бүрийн зургийг {`${BOOK_PAGE_IMAGE_WIDTH_PX}×${BOOK_PAGE_IMAGE_HEIGHT_PX}`} пиксел (өргөн{' '}
            {BOOK_PAGE_IMAGE_WIDTH_PX}) хэмжээтэй бэлтгэнэ үү. Форм болон «Урьдчилан харах» дээрх зураг нь суурь
            харьцааг хадгалж томрох буюу багасгана; сунгахгүй.
          </Alert>
          <BookFormStepPages
            enabledLanguages={enabledLanguages}
            sortedPages={sortedPages}
            selectedPageClientId={selectedPageClientId}
            onSelectPage={setSelectedPageClientId}
            selectedPage={selectedPage}
            canTranslatePageFromEn={canTranslatePageFromEn}
            pageTranslating={pageTranslating}
            pageTranslateError={pageTranslateError}
            onDismissPageTranslateError={() => setPageTranslateError(null)}
            onTranslatePageTextsFromEn={handleTranslatePageTextsFromEn}
            addPage={addPage}
            duplicateSelectedPage={duplicateSelectedPage}
            deletePageByClientId={deletePageByClientId}
            onReorderPages={reorderPagesByClientIds}
            updatePageByClientId={updatePageByClientId}
            setPageFullText={setPageFullText}
            stepNavigation={stepNavigation}
          />
        </>
      )}
    </Stack>
  );

  if (hideBookPreviewChrome) {
    return formBody;
  }

  return (
    <Box sx={{ position: 'relative', pr: { xs: 5, sm: 6 } }}>
      <Tooltip title="Урьдчилан харах">
        <IconButton
          type="button"
          color="primary"
          onClick={() => setBookPreviewOpen(true)}
          aria-label="Урьдчилан харах"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 2,
          }}
        >
          <MenuBookOutlined />
        </IconButton>
      </Tooltip>
      {formBody}
    </Box>
  );
}
